import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, ShoppingCart, Check, Truck, X, Trash2, ExternalLink, Car, Edit, Sparkles, AlertTriangle } from "lucide-react";
import AiOrderImport from "../components/ai/AiOrderImport";
// icon CarFront below imported inline
import PageTransition from "../components/PageTransition";
import SkeletonLoader from "../components/SkeletonLoader";
import { MACHINE_STATUS_LABELS, MACHINE_STATUS_COLORS } from "../constants";

const STATUSES = ["open","in_progress","completed","cancelled"];
const STATUS_LABELS = { open: "Otevřená", in_progress: "Ve zpracování", completed: "Dokončená", cancelled: "Zrušená" };
const STATUS_COLORS = { open: "#FFA726", in_progress: "#42A5F5", completed: "#66BB6A", cancelled: "#EF5350" };
const MT_COLORS = { TR3: "#1565C0", TR4: "#6A1B9A", PC: "#E65100" };

const machineBadgeStyle = (status) => {
  const c = MACHINE_STATUS_COLORS[status] || { bg: "#F5F5F5", color: "#666" };
  return { fontSize: "11px", padding: "1px 8px", borderRadius: "10px", fontWeight: 600, background: c.bg, color: c.color, whiteSpace: "nowrap" };
};

const Orders = () => {
  const [searchParams] = useSearchParams();
  const openParam = searchParams.get("open");
  const [batches, setBatches] = useState([]);
  const [unlinked, setUnlinked] = useState([]);
  const [syncBusy, setSyncBusy] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [partsList, setPartsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [itemsOpen, setItemsOpen] = useState(null);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [aiImportBatch, setAiImportBatch] = useState(null);
  const [form, setForm] = useState({ customer_id: "", machine_type: "", status: "open", note: "" });

  const fetchData = useCallback(async () => {
    try {
      const [bRes, cRes, pRes, uRes] = await Promise.all([
        fetch("/api/orders", { credentials: "include" }).then(r => r.json()),
        fetch("/api/customers?limit=500", { credentials: "include" }).then(r => r.json()),
        fetch("/api/parts?limit=500", { credentials: "include" }).then(r => r.json()),
        fetch("/api/orders/unlinked-machines", { credentials: "include" }).then(r => r.json()).catch(() => ({ success: false })),
      ]);
      if (bRes.success) setBatches(bRes.data);
      if (cRes.success) setCustomers(cRes.data);
      if (pRes.success) setPartsList(pRes.data);
      if (uRes.success) setUnlinked(uRes.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchItems = async (batchId) => {
    try {
      const res = await fetch("/api/orders/" + batchId + "/items", { credentials: "include" });
      const json = await res.json();
      if (json.success) setItems(json.data);
    } catch (e) { console.error(e); }
  };

  // ?open=<batchId> - expand that order on load
  useEffect(() => {
    if (!openParam || loading) return;
    const batchId = parseInt(openParam, 10);
    if (!batchId) return;
    setItemsOpen(batchId);
    fetchItems(batchId);
    const el = document.getElementById("order-" + batchId);
    if (el) el.scrollIntoView({ block: "start" });
  }, [openParam, loading]);

  const openCreate = () => { setEditing(null); setForm({ customer_id: "", machine_type: "", status: "open", note: "" }); setModalOpen(true); };

  const openEdit = (b) => { setEditing(b); setForm({ customer_id: b.customer_id || "", machine_type: b.machine_type || "", status: b.status || "open", note: b.note || "" }); setModalOpen(true); };


  const handleSave = async () => {
    try {
      const url = editing ? "/api/orders/" + editing.id : "/api/orders";
      const method = editing ? "PUT" : "POST";
      const payload = { customer_id: form.customer_id ? parseInt(form.customer_id) : null, machine_type: form.machine_type || null, status: form.status, note: form.note };
      const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(payload) });
      const json = await res.json();
      if (json.success) { 
        setToast({ type: "success", msg: editing ? "Uloženo" : "Objednávka vytvořena" }); 
        setModalOpen(false); 
        fetchData();
        if (!editing && json.data) { setItemsOpen(json.data.id); fetchItems(json.data.id); }
      } else { setToast({ type: "error", msg: json.error || "Chyba" }); }
    } catch (e) { setToast({ type: "error", msg: e.message }); }
  };

  const autoFillParts = async (batchId) => {
    const batch = batches.find(b => b.id === batchId);
    if (!batch?.machine_type) return;
    try {
      const filtered = partsList.filter(p => (p.machine_types || "").split(", ").includes(batch.machine_type));
      for (const part of filtered) {
        const qty = part.qty_per_machine || 1;
        await fetch("/api/orders/" + batchId + "/items", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ part_id: part.id, qty, supplier_id: part.supplier_id || null }) });
      }
      await fetchItems(batchId);
      setToast({ type: "success", msg: "Přidáno " + filtered.length + " dílů" });
    } catch (e) { setToast({ type: "error", msg: e.message }); }
  };

  const markAllDelivered = async (batchId) => {
    if (!confirm("Označit všechny položky jako dodané?")) return;
    try {
      const undelivered = items.filter(i => !i.delivered_at);
      for (const item of undelivered) {
        await fetch("/api/orders/" + batchId + "/items/" + item.id + "/deliver", { method: "POST", credentials: "include" });
      }
      await fetchItems(batchId);
      setToast({ type: "success", msg: "Označeno " + undelivered.length + " položek" });
    } catch (e) { setToast({ type: "error", msg: e.message }); }
  };


  const toggleDelivered = async (batchId, item) => {
    try {
      if (item.delivered_at) {
        await fetch("/api/orders/" + batchId + "/items/" + item.id, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ delivered_at: null }) });
      } else {
        await fetch("/api/orders/" + batchId + "/items/" + item.id + "/deliver", { method: "POST", credentials: "include" });
      }
      fetchItems(batchId);
    } catch (e) { setToast({ type: "error", msg: e.message }); }
  };

  const syncMachines = async (machineId) => {
    setSyncBusy(machineId || "all");
    try {
      const res = await fetch(machineId ? "/api/orders/from-machine/" + machineId : "/api/orders/sync-machines", { method: "POST", credentials: "include" });
      const json = await res.json();
      if (json.success) {
        const created = machineId ? (json.created ? 1 : 0) : (json.created || 0);
        const linked = machineId ? (json.linked ? 1 : 0) : (json.linked || 0);
        setToast({ type: "success", msg: "Vytvořeno objednávek: " + created + (linked ? ", napojeno: " + linked : "") });
        await fetchData();
      } else { setToast({ type: "error", msg: json.error || "Chyba" }); }
    } catch (e) { setToast({ type: "error", msg: e.message }); }
    setSyncBusy(null);
  };

  const handleCreateMachine = async (id) => {
    try {
      const res = await fetch("/api/orders/" + id + "/create-machine", { method: "POST", credentials: "include" });
      const json = await res.json();
      if (json.success) { setToast({ type: "success", msg: json.data.linked ? "Napojeno na existující trenažér " + json.data.serial_number : "Trenaž\u00e9r vytvořen: " + json.data.serial_number }); fetchData(); }
      else { setToast({ type: "error", msg: json.error || "Chyba" }); }
    } catch (e) { setToast({ type: "error", msg: e.message }); }
  };

  const handleDelete = async () => {
    try { await fetch("/api/orders/" + deleteId, { method: "DELETE", credentials: "include" }); setDeleteId(null); fetchData(); setItemsOpen(null); setToast({ type: "success", msg: "Smazáno" }); } catch (e) {}
  };

  const getStatusStyle = (s) => ({ fontSize: "12px", padding: "2px 10px", borderRadius: "12px", background: (STATUS_COLORS[s]||"#ccc")+"22", color: STATUS_COLORS[s]||"#666", fontWeight: 600 });

  return React.createElement(PageTransition, null,
    React.createElement(AiOrderImport, {
      batchId: aiImportBatch,
      open: aiImportBatch !== null,
      onClose: () => setAiImportBatch(null),
      onApplied: (result) => {
        const batchId = aiImportBatch;
        setToast({ type: "success", msg: "Doklad zpracován: " + (result?.delivered || 0) + " dodáno, " + (result?.added || 0) + " přidáno" });
        if (batchId !== null) fetchItems(batchId);
        fetchData();
      },
    }),
    deleteId && React.createElement("div", { style: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 } },
      React.createElement("div", { style: { background: "#fff", borderRadius: "12px", padding: "24px", width: "90%", maxWidth: "400px", textAlign: "center" } },
        React.createElement("h3", { style: { margin: "0 0 8px" } }, "Smazat objedn\ávku"),
        React.createElement("p", { style: { color: "#666", margin: "0 0 16px" } }, "Opravdu chcete smazat tuto objedn\ávku?"),
        React.createElement("div", { style: { display: "flex", gap: "8px", justifyContent: "center" } },
          React.createElement("button", { onClick: () => setDeleteId(null), style: { padding: "8px 20px", border: "1px solid #E0E0E0", borderRadius: "8px", background: "#fff", cursor: "pointer" } }, "Zru\šit"),
          React.createElement("button", { onClick: handleDelete, style: { padding: "8px 20px", border: "none", borderRadius: "8px", background: "#E53935", color: "#fff", cursor: "pointer" } }, "Smazat")
        )
      )
    ),
    toast && React.createElement("div", { onClick: () => setToast(null), style: { position: "fixed", top: "20px", right: "20px", zIndex: 2000, background: toast.type==="error"?"#F44336":"#4CAF50", color: "#fff", padding: "12px 20px", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 600, boxShadow: "0 4px 12px rgba(0,0,0,0.2)", maxWidth: "400px" } }, toast.msg),
    React.createElement("div", { style: { padding: "24px", maxWidth: "1300px", margin: "0 auto" } },
      React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" } },
        React.createElement("h1", { style: { fontSize: "24px", fontWeight: 700, color: "#333", margin: 0 } }, "Objednávky"),
        React.createElement("button", { onClick: openCreate, style: { display: "flex", alignItems: "center", gap: "8px", padding: "10px 18px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 600 } },
          React.createElement(Plus, { size: 18 }), " Nová objednávka"
        )
      ),
      !loading && unlinked.length > 0 && React.createElement("div", { style: { background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: "12px", padding: "16px", marginBottom: "16px" } },
        React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px", marginBottom: "8px" } },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px", color: "#E65100", fontWeight: 700, fontSize: "15px" } },
            React.createElement(AlertTriangle, { size: 18 }), "Trenažéry bez objednávky (" + unlinked.length + ")"
          ),
          React.createElement("button", { onClick: () => syncMachines(null), disabled: syncBusy !== null, style: { padding: "6px 14px", border: "none", borderRadius: "6px", background: syncBusy !== null ? "#ccc" : "#E53935", color: "#fff", cursor: syncBusy !== null ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 } }, syncBusy === "all" ? "Vytvářím…" : "Vytvořit všechny")
        ),
        unlinked.map((m) =>
          React.createElement("div", { key: m.id, style: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px", padding: "8px 0", borderTop: "1px solid #FFE082" } },
            React.createElement("div", { style: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 10px", fontSize: "13px", color: "#555" } },
              React.createElement(Link, { to: "/machines/" + m.id, style: { color: "#1976D2", fontWeight: 600, textDecoration: "none" } }, m.serial_number),
              m.model_type && React.createElement("span", null, m.model_type),
              React.createElement("span", null, m.customer_name || "Bez zákazníka"),
              React.createElement("span", { style: machineBadgeStyle(m.status) }, MACHINE_STATUS_LABELS[m.status] || m.status)
            ),
            React.createElement("button", { onClick: () => syncMachines(m.id), disabled: syncBusy !== null, style: { padding: "6px 14px", border: "1px solid #E53935", borderRadius: "6px", background: "#fff", color: syncBusy !== null ? "#999" : "#E53935", cursor: syncBusy !== null ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 } }, syncBusy === m.id ? "Vytvářím…" : "Vytvořit objednávku")
          )
        )
      ),
      loading ? React.createElement(SkeletonLoader, { count: 3 }) :
        React.createElement("div", { style: { display: "grid", gap: "12px" } },
          batches.length === 0 && React.createElement("div", { style: { background: "#fff", borderRadius: "8px", padding: "24px", textAlign: "center", color: "#999", border: "1px solid #E0E0E0" } }, "Žádné objednávky"),
          batches.map((b) =>
            React.createElement("div", { key: b.id, id: "order-" + b.id, style: { background: "#fff", borderRadius: "8px", padding: "16px", border: "1px solid #E0E0E0" } },
              React.createElement("div", { onClick: () => { setItemsOpen(itemsOpen===b.id?null:b.id); if(itemsOpen!==b.id) fetchItems(b.id); }, style: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px", cursor: "pointer" } },
                React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "12px" } },
                  React.createElement(ShoppingCart, { size: 20, style: { color: STATUS_COLORS[b.status] || "#ccc" } }),
                  React.createElement("div", null,
                    React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px" } },
                      React.createElement("strong", null, b.order_number || ("#"+b.id)),
                      b.machine_type && React.createElement("span", { style: { fontSize: "11px", padding: "1px 6px", borderRadius: "8px", fontWeight: 600, color: MT_COLORS[b.machine_type] || "#666", background: (MT_COLORS[b.machine_type]||"#eee")+"22" } }, b.machine_type),
                      React.createElement("span", { style: getStatusStyle(b.status) }, STATUS_LABELS[b.status] || b.status)
                    ),
                    React.createElement("div", { style: { fontSize: "13px", color: "#666", marginTop: "2px" } },
                      b.customer_name || "Bez zákazníka",
                      " | ", b.item_count || 0, " položek",
                      b.delivered_count > 0 ? " (" + b.delivered_count + " dodáno)" : ""
                    ),
                    b.machine_id && React.createElement("div", { style: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px", fontSize: "13px", color: "#666", marginTop: "4px" } },
                      "Trenažér: ",
                      React.createElement(Link, { to: "/machines/" + b.machine_id, onClick: (e) => e.stopPropagation(), style: { color: "#1976D2", fontWeight: 600, textDecoration: "none" } }, b.machine_serial || ("#" + b.machine_id)),
                      b.machine_status && React.createElement("span", { style: machineBadgeStyle(b.machine_status) }, MACHINE_STATUS_LABELS[b.machine_status] || b.machine_status)
                    )
                  )
                ),
                React.createElement("div", { style: { display: "flex", gap: "4px" } },
                  b.status === "completed" && !b.machine_id && React.createElement("button", { onClick: (e) => { e.stopPropagation(); handleCreateMachine(b.id); }, title: "Vytvořit trenažér", style: { padding: "6px 10px", border: "1px solid #66BB6A", borderRadius: "6px", background: "#E8F5E9", cursor: "pointer", color: "#2E7D32" } }, React.createElement(Car, { size: 14 })),
                  React.createElement("button", { onClick: (e) => { e.stopPropagation(); openEdit(b); }, title: "Upravit", style: { padding: "6px 10px", border: "1px solid #E0E0E0", borderRadius: "6px", background: "#fff", cursor: "pointer" } }, React.createElement(Edit, { size: 14 })),
                  React.createElement("button", { onClick: (e) => { e.stopPropagation(); setDeleteId(b.id); }, style: { padding: "6px 10px", border: "1px solid #E0E0E0", borderRadius: "6px", background: "#fff", cursor: "pointer" } }, React.createElement(Trash2, { size: 14 }))
                )
              ),
              itemsOpen === b.id && React.createElement("div", { style: { marginTop: "12px", padding: "12px", background: "#f9f9f9", borderRadius: "8px" } },
                React.createElement("div", { style: { marginBottom: "8px", display: "flex", gap: "8px", flexWrap: "wrap" } },
                  b.machine_type && items.length === 0 && React.createElement("button", { onClick: () => autoFillParts(b.id), style: { padding: "6px 14px", border: "none", borderRadius: "6px", background: "#E53935", color: "#fff", cursor: "pointer", fontSize: "13px" } }, "Doplnit díly pro " + b.machine_type),
                  items.some(i => !i.delivered_at) && React.createElement("button", { onClick: () => markAllDelivered(b.id), style: { padding: "6px 14px", border: "none", borderRadius: "6px", background: "#66BB6A", color: "#fff", cursor: "pointer", fontSize: "13px" } }, "✓ Označit vše dodáno"),
                  React.createElement("button", { onClick: () => setAiImportBatch(b.id), title: "Nahrát fakturu nebo dodací list – AI spáruje položky s díly", style: { display: "inline-flex", alignItems: "center", gap: "6px", padding: "6px 14px", border: "none", borderRadius: "6px", background: "#F3E5F5", color: "#7B1FA2", cursor: "pointer", fontSize: "13px", fontWeight: 500 } },
                    React.createElement(Sparkles, { size: 14 }), "Načíst doklad (AI)"
                  )
                ),
                items.map((item) =>
                  React.createElement("div", { key: item.id, style: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #eee" } },
                    React.createElement("label", { style: { display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", flex: 1 } },
                      React.createElement("input", { type: "checkbox", checked: !!item.delivered_at, onChange: () => toggleDelivered(b.id, item), style: { width: "16px", height: "16px" } }),
                      React.createElement("div", null,
                        React.createElement("strong", { style: { fontSize: "14px" } }, item.part_name || ("Díl #"+item.part_id)),
                        React.createElement("div", { style: { fontSize: "12px", color: "#666" } },
                          item.supplier_name && React.createElement("span", null, "Dodavatel: ", item.supplier_name, " | "),
                          "Ks: ", item.qty,
                          item.delivered_at && React.createElement("span", { style: { color: "#66BB6A", marginLeft: "8px" } }, "\u2713 Dodáno")
                        )
                      )
                    ),
                    item.delivered_at && React.createElement("span", { style: { fontSize: "12px", color: "#999" } }, new Date(item.delivered_at).toLocaleDateString("cs"))
                  )
                )
              )
            )
          )
        ),
      modalOpen && React.createElement("div", { style: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 } },
        React.createElement("div", { style: { background: "#fff", borderRadius: "12px", padding: "24px", width: "90%", maxWidth: "480px", maxHeight: "90vh", overflow: "auto" } },
          React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" } },
            React.createElement("h2", { style: { margin: 0, fontSize: "18px" } }, editing ? "Upravit objednávku" : "Nová objednávka"),
            React.createElement("button", { onClick: () => setModalOpen(false), style: { background: "none", border: "none", cursor: "pointer" } }, React.createElement(X, { size: 20 }))
          ),
          React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "12px" } },
            React.createElement("label", { style: { fontSize: "13px", fontWeight: 600, color: "#555" } }, "Zákazník",
              React.createElement("select", { value: form.customer_id, onChange: (e) => setForm({ ...form, customer_id: e.target.value }), style: { width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #E0E0E0", fontSize: "14px", marginTop: "4px" } },
                React.createElement("option", { value: "" }, "--- Vyberte ---"),
                customers.map(c => React.createElement("option", { key: c.id, value: c.id }, c.company_name))
              )
            ),
            React.createElement("label", { style: { fontSize: "13px", fontWeight: 600, color: "#555" } }, "Typ trenažéru",
              React.createElement("div", { style: { display: "flex", gap: "8px", marginTop: "4px" } },
                ["TR3","TR4","PC"].map(t => React.createElement("button", { key: t, type: "button", onClick: () => setForm({ ...form, machine_type: form.machine_type===t?"":t }), style: { padding: "6px 14px", borderRadius: "20px", border: "1px solid "+(MT_COLORS[t]||"#ccc"), background: form.machine_type===t ? (MT_COLORS[t]||"#eee")+"22" : "#fff", color: form.machine_type===t ? MT_COLORS[t]||"#333" : "#666", cursor: "pointer", fontSize: "13px", fontWeight: 500 } }, t))
              )
            ),
            React.createElement("label", { style: { fontSize: "13px", fontWeight: 600, color: "#555" } }, "Stav",
              React.createElement("select", { value: form.status, onChange: (e) => setForm({ ...form, status: e.target.value }), style: { width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #E0E0E0", fontSize: "14px", marginTop: "4px" } },
                STATUSES.map(s => React.createElement("option", { key: s, value: s }, STATUS_LABELS[s]))
              )
            ),
            React.createElement("label", { style: { fontSize: "13px", fontWeight: 600, color: "#555" } }, "Poznámka", React.createElement("textarea", { value: form.note, onChange: (e) => setForm({ ...form, note: e.target.value }), rows: 2, style: { width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #E0E0E0", fontSize: "14px", marginTop: "4px", resize: "vertical" } })),
            React.createElement("div", { style: { display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" } },
              React.createElement("button", { onClick: () => setModalOpen(false), style: { padding: "10px 20px", border: "1px solid #E0E0E0", borderRadius: "8px", background: "#fff", cursor: "pointer", fontSize: "14px" } }, "Zrušit"),
              React.createElement("button", { onClick: handleSave, disabled: !form.machine_type, style: { padding: "10px 20px", border: "none", borderRadius: "8px", background: form.machine_type ? "#E53935" : "#ccc", color: "#fff", cursor: form.machine_type ? "pointer" : "not-allowed", fontSize: "14px", fontWeight: 600 } }, editing ? "Uložit" : "Vytvořit")
            )
          )
        )
      )
    )
  );
};

export default Orders;
